import React from 'react';
import { format, parse } from 'date-fns';
import Button from '@hero/ui-kit/inputs/Button';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';

import useCareHistory from '../api/useCareHistory';
import useCareActorName, { ActorName } from '../useCareActorName';
import { careErrorText, careReasonText } from '../constants';
import { dateTime, timeText } from '../presentation';
import { patientLabel } from '../rowSets';
import * as Style from '../style.module.scss';
import { CareHistoryEvent, CareRow } from '../types';

const EVENT_LABELS: Record<string, string> = {
    candidate_created: 'Added to the billing month',
    candidate_updated: 'Billing information updated',
    candidate_retired: 'Removed from the billing month',
    enrollment_changed: 'Enrollment updated',
    coverage_recorded: 'Minutes CSV updated',
    evidence_recorded: 'Billing result updated',
    decision_recorded: 'Billing decision updated',
    decision_invalidated: 'Previous decision no longer applies',
    result_invalidated: 'Billing result needs updating',
    note_changed: 'Note updated',
    hold_set: 'Billing blocked',
    hold_cleared: 'Billing unblocked',
    release_approved: 'Enrollment-gap billing approved',
    release_revoked: 'Billing approval removed'
};

const USER_ACTION_EVENTS = new Set([
    'decision_recorded',
    'note_changed',
    'hold_set',
    'hold_cleared',
    'release_approved',
    'release_revoked'
]);
const EVENT_DESCRIPTIONS: Record<string, string> = {
    coverage_recorded: 'This patient’s presence and care time in the minutes CSV were updated.',
    evidence_recorded: 'Billing details were saved after qualification.',
    enrollment_changed: 'Population sync found a change in the enrollment records.',
    decision_invalidated:
        'The patient’s information changed. The previous decision no longer applies.',
    result_invalidated:
        'The recorded information changed, so the previous billing result was cleared.'
};
const FIELD_LABELS: Record<string, string> = {
    note: 'Note',
    status: 'Billing result',
    billable: 'Billable',
    units: 'Units to bill',
    operator_hold: 'Billing block',
    release_approval: 'Approval reference',
    outcome: 'Decision',
    matched_presence: 'Patient found in minutes CSV',
    rpm_indicated: 'RPM time reported',
    candidate_active: 'Included in this billing month',
    service_date: 'Billing date',
    reason: 'Reason',
    result_reason: 'Billing reason',
    program: 'Program',
    role: 'Code type',
    price: 'Price',
    icd10_code: 'Condition code',
    upstream_editor: 'Enrollment changed by',
    minutes: 'Care time',
    raw_seconds: 'Time in CSV',
    eligible_seconds: 'Time that counts',
    dropped_seconds: 'Time not counted',
    activity_seconds: 'Recorded time',
    eligible_seconds_by_stream: 'Time by program',
    lineage: 'Enrollment periods',
    result: 'Billing outcome',
    program_enrollment_date: 'Joined program',
    program_disenrollment_date: 'Left program',
    member_activated_at: 'Membership started',
    member_discharged_at: 'Membership ended',
    member_cancelled_at: 'Membership cancelled',
    eligible_start: 'Care counted from',
    eligible_end: 'Care counted until',
    conditions: 'Conditions',
    items: 'Condition records',
    condition_count: 'Number of conditions',
    rpm_seconds: 'RPM time',
    state: 'Record availability',
    historical_applicability: 'Applies to this month',
    criterion_state: 'Condition check',
    icd_code: 'Condition code',
    date_onset: 'Condition began',
    date_diagnosis: 'Diagnosed on',
    date_changed: 'Updated on',
    condition_confirmation: 'Condition review',
    statement_confirmed: 'Confirmed by reviewer',
    warning_kinds: 'Review items',
    changed_fields: 'Fields updated',
    'RTM MSK': 'RTM musculoskeletal',
    'RTM RESP': 'RTM respiratory',
    CCM: 'CCM',
    BHI: 'BHI'
};
const VALUE_LABELS: Record<string, string> = {
    yes: 'Billable',
    no: 'Not billable',
    no_addon: 'Not billable',
    incomplete: 'Needs review',
    not_graded: 'Not calculated',
    hold: 'Blocked',
    block: 'Blocked',
    billable: 'Billable',
    not_billable: 'Not billable',
    base: 'First code',
    addon: 'Additional code',
    not_assessed: 'Not checked',
    not_established: 'Not confirmed for this month',
    operator_hold: 'Billing blocked',
    operator_block: 'Reviewer chose not to bill',
    base_not_billable: 'The first code is not billable',
    below_threshold: 'Not enough care time',
    below_addon_threshold: 'Not enough additional care time',
    missing_from_minutes_csv: 'Patient missing from minutes CSV',
    same_program_reenrollment_requires_review: 'Enrollment change needs review',
    rpm_concurrency_review_required: 'RTM and RPM billing needs review',
    condition_review_required: 'Conditions need review',
    rtm_cross_org_month_conflict: 'Another organization locked RTM billing',
    no_longer_eligible: 'No longer enrolled for this billing period'
};
const SOURCE_LABELS: Record<string, string> = {
    operator: 'Portal action or CSV import',
    population_run: 'Population sync',
    qualification_run: 'Billing calculation'
};
const TECHNICAL_FIELDS =
    /(?:^schema_version$|^baseline$|^action$|^review_scope$|^change_kind$|_id$|^rule_version$|^contract_version$)/;
const eventTitle = (event: CareHistoryEvent): string => {
    if (event.event_type === 'decision_recorded') {
        if (event.after.action === 'exclude' || event.after.outcome === 'not_billable')
            return 'Marked not billable';
        if (event.after.action === 'clear_exclusion') return 'Exclusion removed';
        if (event.after.outcome === 'billable') return 'Approved for billing';
    }
    return (
        EVENT_LABELS[event.event_type] ??
        careReasonText(event.event_type) ??
        'Billing information updated'
    );
};

// Render only the backend's display projection. Raw source payloads are never requested.
const Facts: React.FC<{
    facts: Record<string, unknown>;
    actorName: ActorName;
    technical?: boolean;
    context?: string;
}> = ({ facts, actorName, technical = false, context }) => {
    const entries = Object.entries(facts).filter(
        ([key]) => technical || !TECHNICAL_FIELDS.test(key)
    );
    return entries.length ? (
        <dl className={Style.factList}>
            {entries.map(([key, value]) => (
                <React.Fragment key={key}>
                    <dt>
                        {context === 'items' && key === 'status'
                            ? 'Condition status'
                            : (FIELD_LABELS[key] ?? careReasonText(key))}
                    </dt>
                    <dd>
                        <FactValue
                            field={key}
                            value={value}
                            actorName={actorName}
                            technical={technical}
                            context={context}
                        />
                    </dd>
                </React.Fragment>
            ))}
        </dl>
    ) : (
        <span>No recorded details</span>
    );
};
const FactValue: React.FC<{
    field: string;
    value: unknown;
    actorName: ActorName;
    technical?: boolean;
    context?: string;
}> = ({ field, value, actorName, technical = false, context }) => {
    if (value === undefined) return <>Not recorded</>;
    if (value === null || value === '')
        return (
            <>
                {field === 'note'
                    ? 'No note'
                    : field === 'operator_hold'
                      ? 'Not blocked'
                      : field === 'units'
                        ? 'Not decided'
                        : 'Not recorded'}
            </>
        );
    if (
        technical &&
        (TECHNICAL_FIELDS.test(field) || field === 'upstream_editor') &&
        typeof value !== 'object'
    )
        return <>{String(value)}</>;
    if (field === 'upstream_editor' && (typeof value === 'string' || typeof value === 'number'))
        return <>{actorName(value)}</>;
    if (Array.isArray(value))
        return value.length ? (
            <>
                {value.map((item, index) => (
                    <div key={index} className={Style.historyValue}>
                        {typeof item === 'object' && item !== null ? (
                            <Facts
                                facts={item as Record<string, unknown>}
                                actorName={actorName}
                                technical={technical}
                                context={field}
                            />
                        ) : (
                            <FactValue
                                field={field}
                                value={item}
                                actorName={actorName}
                                technical={technical}
                            />
                        )}
                    </div>
                ))}
            </>
        ) : (
            <>None</>
        );
    if (typeof value === 'object')
        return (
            <Facts
                facts={value as Record<string, unknown>}
                actorName={actorName}
                technical={technical}
                context={field}
            />
        );
    if (
        typeof value === 'number' &&
        (field.endsWith('seconds') || context === 'eligible_seconds_by_stream')
    )
        return <>{timeText(value)}</>;
    if (typeof value === 'boolean') return <>{value ? 'Yes' : 'No'}</>;
    if (
        typeof value === 'string' &&
        /(?:_at|_date|^service_date$|^date_|^eligible_start$|^eligible_end$)/.test(field)
    )
        return (
            <>
                {/^\d{4}-\d{2}-\d{2}$/.test(value)
                    ? format(parse(value, 'yyyy-MM-dd', new Date()), 'MMM d, yyyy')
                    : dateTime(value)}
            </>
        );
    if (field === 'changed_fields' && typeof value === 'string')
        return <>{FIELD_LABELS[value] ?? careReasonText(value)}</>;
    // Free-text notes and reasons are preserved exactly as entered.
    if (field === 'note' || field === 'reason' || field === 'release_approval')
        return <>{String(value)}</>;
    return <>{VALUE_LABELS[String(value)] ?? careReasonText(String(value))}</>;
};

const HistoryPanel: React.FC<{ row: CareRow; month: string }> = ({ row, month }) => {
    const query = useCareHistory(month, row.charge_id, true);
    const events = query.data?.pages.flatMap((page) => page.events) ?? [];
    const { actorName, directoryError, retryNames } = useCareActorName(events.flatMap((event) => [
        event.actor, event.before.upstream_editor, event.after.upstream_editor
    ]).filter((actor): actor is string | number => typeof actor === 'string' || typeof actor === 'number'));
    return (
        <div className={Style.drawerContent}>
            <header className={Style.dialogHeader}>
                <H role="h5" noDefaultMargin>{`${patientLabel(row)} · History`}</H>
                <P noDefaultMargin>
                    {format(parse(month, 'yyyy-MM', new Date()), 'MMMM yyyy')} · Changes to this
                    patient's enrollment, minutes, and billing decisions.
                </P>
            </header>
            {query.isPending ? <P>Loading history…</P> : null}
            {query.error ? (
                <P role="alert" className={Style.apiError}>
                    {careErrorText(query.error)}
                </P>
            ) : null}
            {directoryError ? (
                <P size="small">
                    Team member names could not be loaded.{' '}
                    <Button
                        size="small"
                        variant="outlineSecondary"
                        onClick={() => retryNames()}
                    >
                        Retry names
                    </Button>
                </P>
            ) : null}
            {query.data?.pages.every((page) => !page.events.length) ? (
                <P>No changes recorded for this month.</P>
            ) : null}
            {events.map((event) => {
                const changed = Object.keys({ ...event.before, ...event.after }).filter(
                    (key) =>
                        !TECHNICAL_FIELDS.test(key) &&
                        JSON.stringify(event.before[key]) !== JSON.stringify(event.after[key])
                );
                return (
                    <section key={event.event_id} className={Style.historyEvent}>
                        <div className={Style.historyHeading}>
                            <P strong noDefaultMargin>
                                {eventTitle(event)}
                            </P>
                            <P size="small" noDefaultMargin>
                                {event.program} · CPT {event.code}
                            </P>
                        </div>
                        <P size="small" noDefaultMargin>
                            {actorName(event.actor)} · {dateTime(event.created_at)}
                        </P>
                        {event.reason && USER_ACTION_EVENTS.has(event.event_type) ? (
                            <P size="small" noDefaultMargin>
                                <strong>Reason:</strong> {event.reason}
                            </P>
                        ) : null}
                        {!USER_ACTION_EVENTS.has(event.event_type) &&
                        EVENT_DESCRIPTIONS[event.event_type] ? (
                            <P size="small" noDefaultMargin>
                                {EVENT_DESCRIPTIONS[event.event_type]}
                            </P>
                        ) : null}
                        {changed.length ? (
                            <details className={Style.historyComparison}>
                                <summary>What changed</summary>
                                <div className={Style.historyComparisonScroll}>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th scope="col">Change</th>
                                                <th scope="col">Before</th>
                                                <th scope="col">After</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {changed.map((key) => (
                                                <tr key={key}>
                                                    <th scope="row">
                                                        {FIELD_LABELS[key] ?? careReasonText(key)}
                                                    </th>
                                                    <td>
                                                        <FactValue
                                                            field={key}
                                                            value={event.before[key]}
                                                            actorName={actorName}
                                                        />
                                                    </td>
                                                    <td>
                                                        <FactValue
                                                            field={key}
                                                            value={event.after[key]}
                                                            actorName={actorName}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </details>
                        ) : null}
                        <details className={Style.historyRecord}>
                            <summary>Saved record details</summary>
                            <P size="small">
                                Event {event.event_id} ·{' '}
                                {SOURCE_LABELS[event.source] ?? careReasonText(event.source)} ·
                                Recorded user: {event.actor || 'Not recorded'}
                            </P>
                            {event.reason ? (
                                <P size="small">Recorded reason: {event.reason}</P>
                            ) : null}
                            <P strong size="small">
                                Before
                            </P>
                            <Facts facts={event.before} actorName={actorName} technical />
                            <P strong size="small">
                                After
                            </P>
                            <Facts facts={event.after} actorName={actorName} technical />
                        </details>
                    </section>
                );
            })}
            {query.hasNextPage ? (
                <Button
                    variant="outlineSecondary"
                    disabled={query.isFetchingNextPage}
                    onClick={() => query.fetchNextPage()}
                >
                    {query.isFetchingNextPage ? 'Loading…' : 'Load earlier changes'}
                </Button>
            ) : null}
        </div>
    );
};

export default HistoryPanel;
